import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, ArrowRight } from "lucide-react";
import { Section, Eyebrow, Lede } from "../components/site/Section";
import { getLeaderboard, useAuth } from "../lib/auth";

export default function LeaderboardPage() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [totalChars, setTotalChars] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const d = await getLeaderboard(25);
        setEntries(d.entries || []);
        setTotalChars(d.total_chars || 0);
      } catch { /* ignore */ }
      setLoaded(true);
    })();
  }, []);

  const me = user ? entries.find((e) => e.user_id === user.user_id) : null;

  return (
    <div data-testid="leaderboard-page">
      <Section testId="lb-hero" className="pt-12 md:pt-24">
        <Eyebrow>Archive Leaderboard</Eyebrow>
        <h1 className="font-display text-4xl sm:text-5xl md:text-7xl text-[#EAE0D5] leading-[1] mt-4 max-w-3xl">
          The Keeper&apos;s <span className="text-[#C5A059] italic font-light">Standing</span>.
        </h1>
        <Lede>
          Ranked by total records recovered. Ties broken by who reached the count first.
          Recovering all {totalChars || "??"} qualifies for the Anime NYC trivia round.
        </Lede>

        {me && (
          <div className="mt-7 bg-[#0A0A0A] border border-[#C5A059]/40 p-5 inline-flex flex-wrap items-center gap-5" style={{ boxShadow: "0 0 50px -22px #C5A059" }} data-testid="lb-my-rank">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#A39E99]">Your Rank</div>
              <div className="font-display text-3xl text-[#C5A059] tabular-nums">#{me.rank}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#A39E99]">Records</div>
              <div className="font-display text-2xl text-[#EAE0D5] tabular-nums">{me.unlocks} / {totalChars}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#A39E99]">Accuracy</div>
              <div className="font-display text-2xl text-[#EAE0D5] tabular-nums">{me.accuracy}%</div>
            </div>
          </div>
        )}
      </Section>

      <Section testId="lb-table" className="pt-0 pb-24">
        {!loaded ? (
          <p className="text-sm text-[#737373]">Reading the ledger…</p>
        ) : entries.length === 0 ? (
          <div className="bg-[#0A0A0A] border border-white/5 p-10 md:p-14 text-center" data-testid="lb-empty">
            <Trophy size={20} className="mx-auto text-[#C5A059]" />
            <p className="font-display text-2xl md:text-3xl text-[#EAE0D5] mt-4">
              The ledger is empty.
            </p>
            <p className="text-sm text-[#A39E99] mt-3 max-w-md mx-auto">
              No records have been recovered yet. Be the first name on it.
            </p>
            <Link
              to={user ? "/archive/drop" : "/login"}
              className="mt-6 inline-flex items-center gap-2 bg-[#C5A059] text-black px-6 py-3 text-[11px] tracking-[0.26em] uppercase hover:bg-[#D4AF68]"
              data-testid="lb-empty-cta"
            >
              {user ? "Open the Signal" : "Sign In"} <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="lb-rows">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] tracking-[0.28em] uppercase text-[#737373] border-b border-white/5">
                  <th className="text-left py-3 pr-3 w-12">#</th>
                  <th className="text-left py-3 pr-3">Keeper</th>
                  <th className="text-right py-3 pr-3">Records</th>
                  <th className="text-right py-3 pr-3 hidden sm:table-cell">%</th>
                  <th className="text-right py-3 pr-3 hidden md:table-cell">Accuracy</th>
                  <th className="text-right py-3 pr-3 hidden md:table-cell">Last</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const isMe = user && e.user_id === user.user_id;
                  return (
                    <tr
                      key={e.user_id}
                      className="border-b border-white/5"
                      data-testid={`lb-row-${e.rank}`}
                      style={isMe ? { background: "rgba(197,160,89,0.05)" } : {}}
                    >
                      <td className="py-4 pr-3 font-display text-xl tabular-nums" style={{ color: e.rank <= 3 ? "#C5A059" : "#A39E99" }}>
                        {e.rank}
                      </td>
                      <td className="py-4 pr-3 text-[#EAE0D5]">
                        {e.username}
                        {isMe && <span className="ml-2 text-[10px] tracking-[0.22em] uppercase text-[#C5A059]">You</span>}
                      </td>
                      <td className="py-4 pr-3 text-right text-[#EAE0D5] tabular-nums">{e.unlocks} / {totalChars}</td>
                      <td className="py-4 pr-3 text-right text-[#A39E99] tabular-nums hidden sm:table-cell">{e.completion_pct}%</td>
                      <td className="py-4 pr-3 text-right text-[#A39E99] tabular-nums hidden md:table-cell">{e.accuracy}%</td>
                      <td className="py-4 pr-3 text-right text-[10px] tracking-[0.2em] uppercase text-[#737373] hidden md:table-cell">
                        {e.last_discovery ? new Date(e.last_discovery).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}
